import { portfolioData } from "@/data/portfolioData"
import { skillIcons } from "@/data/icons"
import { Badge } from "@/components/ui-lab/badge"
import { Button } from "@/components/ui-lab/button"
import { Card, CardContent, CardFooter } from "@/components/ui-lab/card"
import { cn } from "@/lib/utils"
import { FaFolderOpen, FaGithub, FaExternalLinkAlt } from "react-icons/fa"
import { TagIcon } from "../custom/Tag"

const ribbonColors: Record<string, string> = {
  orange: "bg-orange-700",
  green: "bg-green-700",
  purple: "bg-purple-600",
  blue: "bg-blue-600",
}

export const MyProjects = () => {
  const { projects } = portfolioData

  return (
    <>
      <h2 className="text-3xl font-semibold mb-6 flex items-center gap-2" suppressHydrationWarning>
        <FaFolderOpen /> Projects
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.name} className="relative h-full">
            <span
              className={cn(
                "absolute -right-2 top-2 z-10 rounded-l-md rounded-tr-md px-2 py-0.5 text-sm text-white shadow",
                ribbonColors[project.badge.color] ?? "bg-primary"
              )}
            >
              {project.badge.label}
            </span>
            <Card className="h-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:border-teal-500 border-2 border-transparent flex flex-col">
              <CardContent className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-semibold mb-2 pr-16">
                  {project.live || project.github ? (
                    <a
                      href={project.live ?? project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
                    >
                      {project.name}
                    </a>
                  ) : (
                    project.name
                  )}
                </h3>
                <Badge
                  variant="outline"
                  className={cn(
                    "mb-3 w-fit font-normal",
                    project.type === "Company"
                      ? "border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                      : "border-purple-300 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950/60 dark:text-purple-300"
                  )}
                >
                  {project.type} Project
                </Badge>
                <p className="text-muted-foreground text-sm mb-3">{project.description}</p>
                {project.highlights && project.highlights.length > 0 && (
                  <div className="mb-3">
                    <span className="font-semibold text-sm">Highlights:</span>
                    <ul className="mt-1 ml-4 text-sm list-disc">
                      {project.highlights.map((h, i) => (
                        <li key={i} className="mb-1 text-gray-600 dark:text-gray-400">
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-auto">
                  {project.technologies.map((tech) => (
                    <Badge
                      key={tech}
                      variant="tech"
                      className="font-normal cursor-default transition-all duration-300 hover:scale-110"
                    >
                      <TagIcon text={tech} icon={skillIcons[tech]} />
                    </Badge>
                  ))}
                </div>
              </CardContent>
              {(project.github || project.live) && (
                <CardFooter className="border-t p-2 justify-around">
                  {project.github && (
                    <Button variant="ghost" asChild>
                      <a href={project.github} target="_blank" rel="noopener noreferrer">
                        <FaGithub /> GitHub
                      </a>
                    </Button>
                  )}
                  {project.live && (
                    <Button variant="ghost" asChild>
                      <a href={project.live} target="_blank" rel="noopener noreferrer follow">
                        <FaExternalLinkAlt /> Live
                      </a>
                    </Button>
                  )}
                </CardFooter>
              )}
            </Card>
          </div>
        ))}
      </div>
    </>
  )
}
